<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  contactIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['completed']);

const { t } = useI18n();
const store = useStore();

const POLL_INTERVAL_MS = 1500;

const dialogRef = ref(null);
const isOpen = ref(false);
const selectedFlowId = ref(null);
const isSubmitting = ref(false);
// Per-contact outcome of the last submit; while present the dialog shows the
// results instead of the form.
const results = ref(null);
// Background run progress while a batch is being processed.
const runProgress = ref(null);
let pollTimer = null;

const flows = useMapGetter('followUpFlows/getFlows');
const currentUserId = useMapGetter('getCurrentUserID');
const activeFlows = computed(() =>
  (flows.value || []).filter(flow => flow.status === 'active')
);

// Set when the shown results were recovered from a run that finished while
// the dialog was closed — offers "start a new batch" alongside them.
const resumedTerminal = ref(false);
// Aggregate counts from the backend: with paginated result rows they are the
// authoritative summary.
const dispositionCounts = ref(null);
// Cursor of the next results page of the run on screen; null = fully shown.
const nextCursor = ref(null);
const viewingRun = ref(null);
// Exclusion flag of the cursor walk: blocks a second click while a page is
// in flight (a double-click would append the same page twice). The token
// identifies the CURRENT walk: a stale page's finally must not release the
// exclusion of a newer one.
const isLoadingMore = ref(false);
let loadMoreToken = 0;
// The user's own terminal batches on the selected flow — any of them can be
// opened from the form, not only the most recent one.
const recentRuns = ref([]);

// Identity of the polling session: open/close bump it, so a response of a
// request still in flight when the dialog closed can neither apply state nor
// schedule another timer into a reopened dialog.
let pollGeneration = 0;

const closePolling = () => {
  clearTimeout(pollTimer);
  pollGeneration += 1;
  isOpen.value = false;
};

onBeforeUnmount(closePolling);

const dispositionLabel = disposition => {
  switch (disposition) {
    case 'created':
      return t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RESULT.CREATED');
    case 'no_conversation':
      return t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RESULT.NO_CONVERSATION');
    case 'already_enrolled':
      return t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RESULT.ALREADY_ENROLLED');
    case 'paused_exists':
      return t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RESULT.PAUSED_EXISTS');
    case 'conversation_not_eligible':
      return t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RESULT.NOT_ELIGIBLE');
    case 'contact_not_found':
      return t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RESULT.CONTACT_NOT_FOUND');
    default:
      return t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RESULT.NOT_ENROLLED');
  }
};

const enrolledCount = computed(
  () =>
    dispositionCounts.value?.created ??
    (results.value || []).filter(result => result.disposition === 'created')
      .length
);
const resultsTotal = computed(
  () =>
    (dispositionCounts.value &&
      Object.values(dispositionCounts.value).reduce((a, b) => a + b, 0)) ||
    (results.value || []).length
);

// A batch answers 202 with a durable run id; the dialog polls its progress
// until the background processor reaches a TERMINAL state — completed shows
// the results, failed stops with an error (never an infinite poll). The
// flow/run pair is captured at poll start, immune to later select changes.
const TERMINAL_HTTP_STATUSES = [401, 403, 404, 422];

const pollRun = async (flowId, runId) => {
  const generation = pollGeneration;
  try {
    const run = await store.dispatch('followUpFlows/fetchEnrollmentRun', {
      flowId,
      runId,
    });
    if (generation !== pollGeneration) return;
    runProgress.value = run;
    if (run.status === 'completed') {
      results.value = run.results;
      dispositionCounts.value = run.disposition_counts || null;
      nextCursor.value = run.next_cursor || null;
      viewingRun.value = { flowId, runId };
      runProgress.value = null;
      isSubmitting.value = false;
      emit('completed', run.results);
      return;
    }
    if (run.status === 'failed') {
      runProgress.value = null;
      isSubmitting.value = false;
      useAlert(t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RUN_FAILED'));
      return;
    }
  } catch (error) {
    if (generation !== pollGeneration) return;
    // Authorization/contract failures never heal — stop instead of polling
    // forever; anything else is transient and keeps polling.
    if (TERMINAL_HTTP_STATUSES.includes(error?.response?.status)) {
      runProgress.value = null;
      isSubmitting.value = false;
      useAlert(
        error?.response?.data?.error ||
          t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.ERROR')
      );
      return;
    }
  }
  pollTimer = setTimeout(() => pollRun(flowId, runId), POLL_INTERVAL_MS);
};

// Reopening the dialog (or reloading the page) finds the user's OWN batches
// on the selected flow: a run still processing is resumed with its progress,
// and one that finished while the dialog was closed opens on its results —
// never someone else's batch nor an automatic label snapshot. The submit is
// blocked while the discovery is in flight.
const isMyBatch = run =>
  run.source === 'lote' && run.requested_by_id === currentUserId.value;

// Opens the per-contact outcomes of one terminal run (any of the recent
// list, not only the newest). The submit stays blocked while the detail is
// in flight, and a stale response (reset, flow switch, close) is dropped.
const viewRun = async (run, generation = pollGeneration) => {
  const flowId = selectedFlowId.value;
  isSubmitting.value = true;
  try {
    const detail = await store.dispatch('followUpFlows/fetchEnrollmentRun', {
      flowId,
      runId: run.id,
    });
    if (generation !== pollGeneration) return;
    results.value = detail.results;
    dispositionCounts.value = detail.disposition_counts || null;
    nextCursor.value = detail.next_cursor || null;
    viewingRun.value = { flowId, runId: run.id };
    resumedTerminal.value = true;
  } catch (error) {
    if (generation !== pollGeneration) return;
    useAlert(
      error?.response?.data?.error ||
        t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.ERROR')
    );
  } finally {
    if (generation === pollGeneration) isSubmitting.value = false;
  }
};

const resumeActiveRun = async () => {
  const flowId = selectedFlowId.value;
  if (!flowId) return;
  const generation = pollGeneration;
  isSubmitting.value = true;
  let keepBusy = false;
  try {
    const runs = await store.dispatch(
      'followUpFlows/fetchEnrollmentRuns',
      flowId
    );
    if (generation !== pollGeneration) return;
    const mine = (runs || []).filter(isMyBatch);
    recentRuns.value = mine.filter(run =>
      ['completed', 'failed'].includes(run.status)
    );
    const activeRun = mine.find(run =>
      ['pending', 'processing'].includes(run.status)
    );
    if (activeRun) {
      runProgress.value = activeRun;
      keepBusy = true; // pollRun releases it on the terminal state
      pollRun(flowId, activeRun.id);
      return;
    }
    const latest = mine[0];
    if (latest?.status === 'completed') {
      await viewRun(latest, generation);
    } else if (latest?.status === 'failed') {
      useAlert(t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RUN_FAILED'));
    }
  } catch {
    // best effort: the form still works without the resume
  } finally {
    if (generation === pollGeneration && !keepBusy) isSubmitting.value = false;
  }
};

// Click handler of the recent list: one detail at a time — a competing
// selection while one is in flight is ignored (two racing details would
// fight for the screen, and the loser's finally could unblock the winner).
const selectRun = run => {
  if (isSubmitting.value) return;
  viewRun(run);
};

// Walks the cursor: appends the next page of the run on screen. Applies
// only when the request's full identity {flowId, runId, cursor} is still
// current — a reset, a flow switch or a newer page invalidates it.
const loadMoreResults = async () => {
  if (!nextCursor.value || !viewingRun.value || isLoadingMore.value) return;
  const generation = pollGeneration;
  const requested = { ...viewingRun.value, cursor: nextCursor.value };
  loadMoreToken += 1;
  const token = loadMoreToken;
  isLoadingMore.value = true;
  try {
    const detail = await store.dispatch('followUpFlows/fetchEnrollmentRun', {
      flowId: requested.flowId,
      runId: requested.runId,
      after: requested.cursor,
    });
    if (
      generation !== pollGeneration ||
      viewingRun.value?.flowId !== requested.flowId ||
      viewingRun.value?.runId !== requested.runId ||
      nextCursor.value !== requested.cursor
    )
      return;
    results.value = [...(results.value || []), ...detail.results];
    nextCursor.value = detail.next_cursor || null;
  } catch (error) {
    if (generation !== pollGeneration) return;
    useAlert(
      error?.response?.data?.error ||
        t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.ERROR')
    );
  } finally {
    // only the CURRENT walk releases the exclusion: the late finally of a
    // page superseded by a reset/switch must not unblock a newer walk.
    if (token === loadMoreToken) isLoadingMore.value = false;
  }
};

// Also bumps the generation: any detail/page/poll still in flight when the
// user resets can no longer touch the fresh form.
const resetToForm = () => {
  pollGeneration += 1;
  loadMoreToken += 1; // orphan any page in flight: its finally becomes a no-op
  isLoadingMore.value = false;
  isSubmitting.value = false; // nothing legitimate survives the generation bump
  results.value = null;
  dispositionCounts.value = null;
  nextCursor.value = null;
  viewingRun.value = null;
  resumedTerminal.value = false;
};

const startNewBatch = resetToForm;

let suppressFlowWatch = false;

const open = () => {
  clearTimeout(pollTimer);
  pollGeneration += 1;
  isOpen.value = true;
  resetToForm();
  runProgress.value = null;
  recentRuns.value = [];
  isSubmitting.value = false;
  suppressFlowWatch = true;
  selectedFlowId.value = activeFlows.value[0]?.id ?? null;
  nextTick(() => {
    suppressFlowWatch = false;
  });
  dialogRef.value?.open();
  resumeActiveRun();
};

defineExpose({ open });

// The catalog may arrive after the dialog opens. Select the first active
// flow then, without overriding a choice the user has already made.
watch(activeFlows, availableFlows => {
  if (!isOpen.value || selectedFlowId.value || !availableFlows.length) return;

  selectedFlowId.value = availableFlows[0].id;
});

// Changing the flow re-runs the discovery for THE SELECTED flow: the old
// flow's fetch still in flight is invalidated (generation bump), its runs
// can never fill the screen of the new selection.
watch(selectedFlowId, () => {
  if (suppressFlowWatch) return;
  clearTimeout(pollTimer);
  pollGeneration += 1;
  resetToForm();
  runProgress.value = null;
  recentRuns.value = [];
  resumeActiveRun();
});

const handleConfirm = async () => {
  if (results.value) {
    dialogRef.value?.close();
    return;
  }
  if (!selectedFlowId.value || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    const response = await store.dispatch('followUpFlows/enrollContacts', {
      flowId: selectedFlowId.value,
      contactIds: props.contactIds,
    });
    if (response.run_id) {
      runProgress.value = {
        status: 'pending',
        processed_count: 0,
        total_count: response.total_count,
      };
      await pollRun(selectedFlowId.value, response.run_id);
    } else {
      results.value = response.results;
      isSubmitting.value = false;
      emit('completed', response.results);
    }
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.ERROR');
    useAlert(message);
    isSubmitting.value = false;
  }
};

onMounted(() => {
  store.dispatch('followUpFlows/get').catch(() => {});
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="
      t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.TITLE', { count: contactIds.length })
    "
    :description="t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.DESCRIPTION')"
    :confirm-button-label="
      results
        ? t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.CLOSE')
        : t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.SUBMIT')
    "
    :disable-confirm-button="!results && !selectedFlowId"
    :is-loading="isSubmitting"
    :show-cancel-button="!results"
    @confirm="handleConfirm"
    @close="closePolling"
  >
    <p v-if="runProgress" class="text-sm text-n-slate-11">
      {{
        t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.PROGRESS', {
          processed: runProgress.processed_count,
          total: runProgress.total_count,
        })
      }}
    </p>
    <template v-else-if="!results">
      <p v-if="!activeFlows.length" class="text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.NO_FLOWS') }}
      </p>
      <div v-else class="flex flex-col gap-3">
        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.FLOW_LABEL') }}
          </span>
          <select
            v-model="selectedFlowId"
            class="reset-base w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-white dark:bg-slate-900 text-n-slate-12"
          >
            <option v-for="flow in activeFlows" :key="flow.id" :value="flow.id">
              {{ flow.name }} — {{ flow.inbox?.name }}
            </option>
          </select>
        </label>
        <div v-if="recentRuns.length" class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RECENT_RUNS') }}
          </span>
          <button
            v-for="run in recentRuns"
            :key="run.id"
            type="button"
            class="reset-base flex items-center justify-between gap-2 text-xs text-n-blue-text hover:underline"
            @click="selectRun(run)"
          >
            <span>
              {{
                t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RECENT_RUN_ITEM', {
                  count: run.total_count,
                  date: new Date(run.created_at).toLocaleString(),
                })
              }}
            </span>
            <span
              :class="
                run.status === 'failed' ? 'text-n-ruby-11' : 'text-n-slate-11'
              "
            >
              {{
                run.status === 'failed'
                  ? t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RECENT_RUN_FAILED')
                  : t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.RECENT_RUN_COMPLETED')
              }}
            </span>
          </button>
        </div>
      </div>
    </template>

    <div v-else class="flex flex-col gap-2">
      <p class="text-sm font-medium text-n-slate-12">
        {{
          t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.SUMMARY', {
            enrolled: enrolledCount,
            total: resultsTotal,
          })
        }}
      </p>
      <button
        v-if="resumedTerminal"
        type="button"
        class="reset-base self-start text-sm font-medium text-n-blue-text hover:underline"
        @click="startNewBatch"
      >
        {{ t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.NEW_BATCH') }}
      </button>
      <ul class="flex flex-col gap-1 max-h-64 overflow-y-auto">
        <li
          v-for="result in results"
          :key="result.contact_id"
          class="flex items-center justify-between gap-3 text-sm"
        >
          <span class="truncate text-n-slate-12">
            {{ result.name || `#${result.contact_id}` }}
          </span>
          <span
            class="shrink-0 text-xs"
            :class="
              result.disposition === 'created'
                ? 'text-n-teal-11'
                : 'text-n-slate-11'
            "
          >
            {{ dispositionLabel(result.disposition) }}
          </span>
        </li>
      </ul>
      <button
        v-if="nextCursor"
        type="button"
        class="reset-base self-start text-xs font-medium text-n-blue-text hover:underline disabled:opacity-50"
        :disabled="isLoadingMore"
        @click="loadMoreResults"
      >
        {{
          t('CONTACTS_LAYOUT.FOLLOW_UP_ENROLL.LOAD_MORE', {
            shown: results.length,
            total: resultsTotal,
          })
        }}
      </button>
    </div>
  </Dialog>
</template>
